import { Typography } from '@iheartjane/reefer';
import type { ReactNode } from 'react';

import { ChoiceChip } from '../../../choiceChip/choiceChip';

import styles from './reviewOptionsField.module.css';

export interface ReviewOption {
  /** Optional leading icon */
  icon?: ReactNode;
  /** Tag id as returned by the API. */
  id: number;
  /** Pre-rendered label text. Caller owns casing, ordering, and i18n. */
  label: string;
}

export interface ReviewOptionsFieldProps {
  /** Helper copy under the legend. Defaults to "Choose up to {maxSelections}". */
  helperText?: string;
  /** Appends "(Optional)" to the legend. */
  isOptional?: boolean;
  /** Group label, e.g. "What is this product good for?". */
  legend: string;
  /** Maximum number of selectable options. */
  maxSelections?: number;
  /** Fired with the full next selection whenever an option is toggled. */
  onChange: (selectedIds: number[]) => void;
  /** Options, rendered in the order given. */
  options: ReviewOption[];
  /** Ids of the currently selected options. */
  selectedIds: number[];
  /** `data-testid` on the wrapper. */
  testId?: string;
}

const DEFAULT_MAX_SELECTIONS = 3;

export function ReviewOptionsField({
  helperText,
  isOptional = false,
  legend,
  maxSelections = DEFAULT_MAX_SELECTIONS,
  onChange,
  options,
  selectedIds,
  testId = 'review-options-field',
}: ReviewOptionsFieldProps) {
  if (options.length === 0) return null;

  const helper = helperText ?? `Choose up to ${maxSelections}`;
  const isMaxed = selectedIds.length >= maxSelections;

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else if (!isMaxed) {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <fieldset data-testid={testId}>
      <Typography as="legend" variant="body-semibold">
        {legend}
        {isOptional && (
          <>
            {' '}
            <Typography as="span" color="grays-mid">
              (Optional)
            </Typography>
          </>
        )}
      </Typography>
      <Typography color="grays-mid">{helper}</Typography>
      <div className={styles['grid']}>
        {options.map(({ icon, id, label }) => {
          const isSelected = selectedIds.includes(id);

          return (
            <ChoiceChip
              key={id}
              disabled={!isSelected && isMaxed}
              icon={icon}
              label={label}
              onClick={() => toggle(id)}
              selected={isSelected}
            />
          );
        })}
      </div>
    </fieldset>
  );
}
